'use client'

// Súper online PR 5 (Tarea 3): la caja de búsqueda con autocompletado. Vive en el marco de las
// páginas del súper (`GroceryShell`) y en la portada SOLO con `online_retail_v2`
// (`RestaurantMicrosite` la pinta únicamente si recibe `searchBox`).
//
// - Sin JS es un formulario normal: GET a `/buscar` del host con `q`.
// - Con JS: 200 ms de espera tras la última tecla, nada por debajo de `SEARCH_MIN_CHARS`, un
//   `AbortController` por tecleo (la respuesta de una consulta vieja nunca pisa la nueva) y 4 s de
//   tope. Cualquier falla (red, 429, 404 de la bandera, 503) deja la lista vacía: nunca un error
//   a la vista, la búsqueda completa sigue a un Enter.
// - Combobox accesible (patrón ARIA 1.2): flechas, Enter, Escape, `aria-activedescendant`.
//
// La consulta se dispara desde el `onChange`, no desde un efecto: así ningún setState corre
// dentro de un efecto y el primer render (servidor) no pide nada.
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { localizedName, type NameTranslations } from '@/lib/menu/localizedName'
import { focalPosition } from '@/lib/menu/photoFocal'
import { storefrontHref } from '@/lib/storefront/href'
import { SEARCH_MAX_CHARS, SEARCH_MIN_CHARS, clampSearchQuery } from '@/lib/storefront/searchLimits'
import { menuPhotoUrl } from '@/lib/utils/imageTransform'

const DEBOUNCE_MS = 200
const TIMEOUT_MS = 4000

type Suggestion = {
  id: string
  handle: string
  name: string
  translations: NameTranslations | null
  image_url: string | null
  image_focal: string | null
}

/** La respuesta de `/api/online-orders/storefront-search`, validada lo justo para pintarla. */
function suggestionsOf(body: unknown): Suggestion[] {
  const items = (body as { items?: unknown } | null)?.items
  if (!Array.isArray(items)) return []
  return items.filter(
    (i): i is Suggestion =>
      !!i && typeof (i as Suggestion).id === 'string' && typeof (i as Suggestion).handle === 'string' && typeof (i as Suggestion).name === 'string',
  )
}

/**
 * ¿El foco sigue dentro de la caja? Una respuesta del autocompletado que llega tarde solo abre
 * la lista si es así: si el usuario ya salió del campo, no le aparece una lista colgando.
 */
export function focusIsInside(
  root: { contains: (node: never) => boolean } | null | undefined,
  activeElement: unknown,
): boolean {
  return !!root && !!activeElement && root.contains(activeElement as never)
}

export function StorefrontSearchBox({
  slug,
  basePath,
  locationId,
  initialQuery = '',
}: {
  /** Slug del local (identifica la caja; los links salen de `basePath`). */
  slug: string
  /** Prefijo público del host (`""`, `/<slug>` u `/order/<slug>`). */
  basePath: string
  locationId: string
  /** La consulta de la página de resultados, para que la caja la muestre. */
  initialQuery?: string
}) {
  const t = useTranslations('public.microsite.grocery')
  const locale = useLocale() === 'en' ? 'en' : 'es'
  const listId = `${useId()}-${slug}-sugerencias`
  const [query, setQuery] = useState(initialQuery)
  const [items, setItems] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const ctrlRef = useRef<AbortController | null>(null)

  /** Corta la espera y la consulta en vuelo (cada tecla, Escape, al irse de la página). */
  function cancelPending() {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    ctrlRef.current?.abort()
    ctrlRef.current = null
  }

  // Al irse de la página no queda ni la espera ni la consulta en vuelo.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      ctrlRef.current?.abort()
    },
    [],
  )

  function clearSuggestions() {
    setItems([])
    setActive(-1)
  }

  function onQueryChange(value: string) {
    setQuery(value)
    cancelPending()
    const q = clampSearchQuery(value)
    if (q.length < SEARCH_MIN_CHARS) {
      clearSuggestions()
      return
    }
    const ctrl = new AbortController()
    ctrlRef.current = ctrl
    timerRef.current = setTimeout(async () => {
      const timeout = setTimeout(() => ctrl.abort(), TIMEOUT_MS)
      try {
        const url = `/api/online-orders/storefront-search?locationId=${encodeURIComponent(locationId)}&q=${encodeURIComponent(q)}`
        const res = await fetch(url, { cache: 'no-store', signal: ctrl.signal })
        const next = res.ok ? suggestionsOf(await res.json()) : []
        if (ctrl.signal.aborted) return
        setItems(next)
        setActive(-1)
        if (focusIsInside(rootRef.current, document.activeElement)) setOpen(true)
      } catch {
        // Red caída, timeout o JSON roto: sin sugerencias. Si la abortó una tecla nueva, esa
        // consulta ya manda; no se toca nada.
        if (!ctrl.signal.aborted || ctrlRef.current === ctrl) clearSuggestions()
      } finally {
        clearTimeout(timeout)
      }
    }, DEBOUNCE_MS)
  }

  const expanded = open && items.length > 0
  const optionId = (i: number) => `${listId}-${i}`
  const productHref = (s: Suggestion) => storefrontHref(basePath, `producto/${s.handle}`)

  function go(href: string) {
    cancelPending()
    setOpen(false)
    window.location.assign(href)
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (expanded && active >= 0 && items[active]) return go(productHref(items[active]))
    const q = clampSearchQuery(query)
    if (q.length < SEARCH_MIN_CHARS) return
    go(storefrontHref(basePath, 'buscar', `?q=${encodeURIComponent(q)}`))
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (items.length === 0) return
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setOpen(true)
      // Sin elegida, ↓ va a la primera y ↑ a la última; después da la vuelta.
      setActive((cur) => (cur < 0 ? (step > 0 ? 0 : items.length - 1) : (cur + step + items.length) % items.length))
    } else if (e.key === 'Escape') {
      if (!expanded) return
      e.preventDefault()
      setOpen(false)
      setActive(-1)
    }
    // Enter lo resuelve el submit del formulario (con o sin sugerencia elegida).
  }

  return (
    <div ref={rootRef} className="relative w-full">
      <form
        role="search"
        action={storefrontHref(basePath, 'buscar')}
        method="get"
        onSubmit={onSubmit}
        className="flex items-center gap-2 rounded-full border px-4 py-2"
        style={{ background: 'var(--ms-surface)', borderColor: 'var(--ms-border)' }}
      >
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 shrink-0" style={{ color: 'var(--ms-text-muted)' }}>
          <circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M13 13l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          name="q"
          value={query}
          maxLength={SEARCH_MAX_CHARS}
          autoComplete="off"
          enterKeyHint="search"
          role="combobox"
          aria-label={t('searchLabel')}
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          placeholder={t('searchPlaceholder')}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={(e) => {
            // Un clic en una sugerencia (dentro de la caja) no cierra la lista antes del link.
            if (!rootRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false)
          }}
          className="min-w-0 flex-1 bg-transparent text-[15px] outline-none"
          style={{ color: 'var(--ms-text)' }}
        />
        <button type="submit" className="sr-only">
          {t('searchSubmit')}
        </button>
      </form>

      <ul
        id={listId}
        role="listbox"
        aria-label={t('searchSuggestions')}
        hidden={!expanded}
        // El foco se queda en la caja: un clic en una sugerencia no la cierra antes del link.
        onMouseDown={(e) => e.preventDefault()}
        className="absolute left-0 right-0 z-30 mt-1.5 max-h-[60vh] overflow-y-auto py-1.5"
        style={{
          background: 'var(--ms-surface)',
          border: '1px solid var(--ms-border)',
          borderRadius: 'var(--ms-radius)',
          boxShadow: 'var(--ms-card-shadow)',
        }}
      >
        {expanded &&
          items.map((s, i) => {
            const name = localizedName({ name: s.name, translations: s.translations }, locale)
            const photo = menuPhotoUrl(s.image_url, 96)
            return (
              <li key={s.id} id={optionId(i)} role="option" aria-selected={i === active}>
                <a
                  href={productHref(s)}
                  tabIndex={-1}
                  onMouseEnter={() => setActive(i)}
                  className="flex items-center gap-3 px-3 py-2 text-[14px] font-semibold"
                  style={{ color: 'var(--ms-text)', background: i === active ? 'var(--ms-surface-2)' : undefined }}
                >
                  <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md" style={{ background: 'var(--ms-surface-2)' }}>
                    {photo && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={photo}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                        style={{ objectPosition: focalPosition(s.image_focal) }}
                      />
                    )}
                  </span>
                  <span className="line-clamp-2">{name}</span>
                </a>
              </li>
            )
          })}
      </ul>
    </div>
  )
}
